import { PhotoLocation } from '@/types/photo-check.type';
import { useEffect, useRef, useState, ReactNode } from 'react';

// Date and time as stamped on the photo, e.g. "3 Nov 2026, 10:42:05 AM"
export const formatPhotoTime = (date: Date) =>
  date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit'
  });

// Writes the time in the bottom-right corner, small enough not to hide what is being checked
function stampTime(canvas: HTMLCanvasElement, text: string) {
  const context = canvas.getContext('2d');
  if (!context) return;
  const size = Math.max(14, Math.round(canvas.width / 45));
  context.font = `600 ${size}px sans-serif`;
  const padding = Math.round(size / 2);
  const width = context.measureText(text).width + padding * 2;
  const height = size + padding * 2;
  context.fillStyle = 'rgba(0, 0, 0, 0.55)';
  context.fillRect(canvas.width - width, canvas.height - height, width, height);
  context.fillStyle = '#fff';
  context.textBaseline = 'middle';
  context.fillText(
    text,
    canvas.width - width + padding,
    canvas.height - height / 2
  );
}

/**
 * Live camera inside the page. Only a photo taken here, now, can be checked: there is no file picker,
 * so old photos or pictures from the internet cannot be used. Each photo gets the time it was taken.
 */
export default function RunPhotoCheckCamera({
  onCapture,
  withLocation,
  actions,
  roundShutter
}: {
  onCapture: (photo: File, takenAt: Date, location?: PhotoLocation) => void;
  // Buttons under the camera, given the shutter: lets the popup put "Take photo" next to its own buttons
  actions?: (capture: () => void, ready: boolean) => ReactNode;
  // A round shutter button over the bottom of the picture, like a phone camera app
  roundShutter?: boolean;
  // The checklist has an area: record where the phone is, kept up to date while the camera is open
  withLocation?: boolean;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(new Date());
  const [location, setLocation] = useState<PhotoLocation>();
  const [locationState, setLocationState] = useState<
    'off' | 'finding' | 'on' | 'blocked' | 'unavailable'
  >('off');

  useEffect(() => {
    if (!withLocation) return;
    if (!navigator.geolocation) {
      setLocationState('unavailable');
      return;
    }
    setLocationState('finding');
    const watch = navigator.geolocation.watchPosition(
      position => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: Math.round(position.coords.accuracy)
        });
        setLocationState('on');
      },
      e => setLocationState(e.code === 1 ? 'blocked' : 'unavailable'),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
    return () => navigator.geolocation.clearWatch(watch);
  }, [withLocation]);

  useEffect(() => {
    let stream: MediaStream | undefined;
    let stopped = false;
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(
          'This browser cannot open the camera. Open this run on your phone in Chrome or Safari.'
        );
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        });
        if (stopped) return stream.getTracks().forEach(t => t.stop());
        if (video.current) {
          video.current.srcObject = stream;
          await video.current.play();
          setReady(true);
        }
      } catch (e: any) {
        setError(
          e?.name === 'NotAllowedError'
            ? 'Camera access is blocked. Allow the camera for this site in your browser settings, then try again.'
            : e?.name === 'NotFoundError'
            ? 'No camera found on this device. Open this run on your phone.'
            : 'Could not open the camera. Close other apps using it and try again.'
        );
      }
    })();
    const clock = setInterval(() => setNow(new Date()), 1000);
    return () => {
      stopped = true;
      clearInterval(clock);
      stream?.getTracks().forEach(t => t.stop());
    };
  }, []);

  function capture() {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    const takenAt = new Date();
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext('2d')?.drawImage(v, 0, 0, canvas.width, canvas.height);
    stampTime(canvas, formatPhotoTime(takenAt));
    canvas.toBlob(
      blob => {
        if (blob) {
          onCapture(
            new File([blob], 'photo.jpg', { type: 'image/jpeg' }),
            takenAt,
            location
          );
        }
      },
      'image/jpeg',
      0.92
    );
  }

  if (error) {
    return (
      <div className="rounded bg-red-50 text-red-800 text-sm p-2">{error}</div>
    );
  }

  return (
    <div className="flex flex-col gap-2 items-center">
      <div className="relative w-full bg-black rounded overflow-hidden">
        <video
          ref={video}
          playsInline
          muted
          className="w-full max-h-[60vh] object-contain"
        />
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
            Opening camera…
          </div>
        )}
        {roundShutter && (
          <button
            type="button"
            aria-label="Take photo"
            onClick={capture}
            disabled={!ready}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 h-16 w-16 rounded-full border-4 border-white bg-transparent flex items-center justify-center disabled:opacity-40 shadow-lg"
          >
            <span className="block h-12 w-12 rounded-full bg-white transition-transform active:scale-90" />
          </button>
        )}
        {ready && (
          <span className="absolute bottom-2 right-2 rounded bg-black/55 px-2 py-0.5 text-xs font-semibold text-white">
            {formatPhotoTime(now)}
          </span>
        )}
      </div>
      {withLocation && (
        <div
          className={
            'text-xs ' +
            (locationState === 'on'
              ? 'text-green-800'
              : locationState === 'finding'
              ? 'text-black/50'
              : 'text-amber-800')
          }
        >
          {locationState === 'on'
            ? `📍 Location on (within ${location?.accuracy ?? '?'} m)`
            : locationState === 'finding'
            ? '📍 Finding your location…'
            : locationState === 'blocked'
            ? '📍 Location is blocked. Allow location for this site, or the photo is marked "No location".'
            : '📍 Location not available. The photo will be marked "No location".'}
        </div>
      )}
      {roundShutter ? null : actions ? (
        actions(capture, ready)
      ) : (
        <button
          type="button"
          className="px-4 py-2 rounded bg-black text-white text-sm disabled:opacity-40"
          onClick={capture}
          disabled={!ready}
        >
          Take photo
        </button>
      )}
    </div>
  );
}
